import React, { useCallback, useEffect, useState } from 'react';

import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import classNames from 'classnames';

import { Box } from '../../Box';
import { useDebounce } from '../../hooks';
import { Icon } from '../../Icon';
import { Label, Preloader } from '../common';
import { useAutocompleteReducer } from '../hooks';
import { Filter } from '../types';

import useStyles from './MultipleSuggestedInput.styles';
import { MultipleSuggestedInputProps } from './MultipleSuggestedInput.types';
import { ListBox } from './ListBox';
import { TextFieldMasked } from './TextFieldMasked';
import { Tag } from './Tag';
import { OptionText } from './OptionText';

const DEBOUNCE_TIMEOUT = 200;

/**
 * MultipleSuggestedInput — компонент, который предоставляет поле ввода с подсказками и множественным выбором.
 * Он поддерживает локальные и загружаемые подсказки, настраиваемое отображение вариантов и всплывающие подсказки.
 *
 * @param {Object} props - Свойства компонента.
 * @param {string} props.name - Атрибут name для поля ввода.
 * @param {string} props.label - Текст метки для поля ввода.
 * @param {string[] | undefined} props.value - Текущее значение поля ввода.
 * @param {(value: string[] | undefined) => void} props.onChange - Функция обратного вызова для обработки изменений значения ввода.
 * @param {() => void} [props.onBlur] - Функция обратного вызова для обработки события потери фокуса.
 * @param {string} [props.placeholder] - Текст плейсхолдера для поля ввода.
 * @param {boolean} [props.disabled=false] - Если true, поле ввода будет отключено.
 * @param {boolean} [props.required=false] - Если true, поле ввода будет отмечено как обязательное.
 * @param {boolean} [props.clearable=false] - Если true, поле ввода будет иметь кнопку очистки.
 * @param {boolean} [props.loading=false] - Если true, поле ввода будет показывать индикатор загрузки.
 * @param {ReactNode | null} [props.tooltip=null] - Содержимое всплывающей подсказки, отображаемой с меткой.
 * @param {boolean} [props.error=false] - Если true, поле ввода будет отмечено как содержащее ошибку.
 * @param {string | ReactNode} [props.helperText] - Вспомогательный текст, отображаемый с полем ввода.
 * @param {(option: string) => React.ReactNode} [props.renderOption] - Пользовательская функция отображения вариантов.
 * @param {number} [props.limit=50] - Максимальное количество элементов для запроса.
 * @param {number} [props.minInputValueLength=1] - Минимальная длина ввода, необходимая для вызова подсказок.
 * @param {string} [props.mask] - Маска, применяемая к полю ввода.
 * @param {'local' | 'load'} props.type - Тип подсказок ('local' или 'load').
 * @param {(filter: Filter, mode: 'get' | 'search') => Promise<void>} [props.loadItems] - Функция для загрузки элементов на основе фильтра.
 * @param {string[]} [props.externalItems] - Внешний список элементов, используемых для подсказок.
 * @returns {JSX.Element} - JSX компонента.
 */

export const MultipleSuggestedInput = (props: MultipleSuggestedInputProps) => {
  const {
    name,
    label,
    value,
    onChange,
    onBlur,
    placeholder,
    disabled = false,
    required = false,
    clearable = false,
    loading = false,
    tooltip,
    error = false,
    helperText,
    renderOption,
    limit = 50,
    minInputValueLength = 1,
    minInputValueLengthForRequest = minInputValueLength,
    mask,
  } = props;

  const { classes } = useStyles();

  const { items, status, hasMore, requestItems, resetItems } =
    useAutocompleteReducer<string>(
      props.type === 'load' ? props.loadItems : undefined,
    );

  const [isLoading, setLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>({
    limit,
    offset: 0,
    query: '',
  });
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const isItemsLoading = isLoading || status === 'loading';
  const debouncedInputValue = useDebounce(inputValue, DEBOUNCE_TIMEOUT) ?? '';

  const handleChange = useCallback(
    (event: React.SyntheticEvent, nextValue: string | null) => {
      if (
        nextValue &&
        nextValue.length >= minInputValueLength &&
        nextValue.trim() !== '' &&
        !value?.includes(nextValue.trim())
      ) {
        onChange([...(value || []), nextValue.trim()]);
        setInputValue('');
        setOpen(false);
        resetItems();
        setFilter({ ...filter, offset: 0, query: '' });
      }
    },
    [onChange, filter, resetItems, value, minInputValueLength],
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, nextValue: string, reason: string) => {
      if (reason === 'reset') {
        setInputValue('');
        setOpen(false);

        return;
      } else {
        setInputValue(nextValue);
        setFilter({ ...filter, query: nextValue });
      }

      if (nextValue?.replace(/_+/g, '').trim()) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    },
    [open, filter],
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    if (open) setOpen(false);
  }, [onBlur, open]);

  const handleReset = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setInputValue('');
    handleBlur();
  };

  const handleOpen = useCallback(() => {
    if (inputValue?.replace(/_+/g, '').trim()) {
      setOpen(true);
    }
  }, [inputValue]);

  const handleDeleteTag = (index: number) => () => {
    if (!value) return;

    onChange([...value.slice(0, index), ...value.slice(index + 1)]);
  };

  const requestNextItems = () => {
    setFilter({
      ...filter,
      offset: filter.offset + filter.limit,
    });
    requestItems?.(filter, 'get');
  };

  useEffect(() => {
    if (
      props.type === 'local' ||
      !open ||
      debouncedInputValue.length < minInputValueLengthForRequest
    ) {
      return;
    }

    const abortRequest = requestItems?.(
      {
        ...filter,
        offset: 0,
        query: debouncedInputValue,
      },
      'search',
    );
    setFilter({
      ...filter,
      offset: filter.limit,
    });
    setLoading(false);

    return () => {
      abortRequest?.();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, debouncedInputValue]);

  const filterOptions = (
    optionList: string[],
    state: { inputValue: string },
  ) => {
    const cleanInput = state.inputValue.replace(/_/g, '').trim();

    const filteredOptions = optionList.filter(
      (option) =>
        option.toLowerCase().includes(cleanInput.toLowerCase()) &&
        option.toLowerCase() !== cleanInput.toLowerCase(),
    );

    return inputValue ? [inputValue, ...filteredOptions] : filteredOptions;
  };

  return (
    <FormControl
      className={classNames(classes.suggestedInput, 'aqa_suggested_input')}
    >
      {label && <Label label={label} required={required} tooltip={tooltip} />}

      <Autocomplete
        value=""
        classes={{
          paper: classes.paper,
          option: classes.option,
        }}
        options={props.type === 'load' ? items : props.externalItems}
        filterOptions={filterOptions}
        fullWidth
        freeSolo
        renderTags={() => null}
        disabled={disabled || loading}
        disableClearable={!clearable}
        clearIcon={
          <Icon name="cancel_circle" color="stroke.secondary" size="20" />
        }
        inputValue={loading ? 'Загрузка...' : inputValue}
        onInputChange={mask ? undefined : handleInputChange}
        onChange={handleChange}
        open={inputValue.length >= minInputValueLengthForRequest ? open : false}
        onOpen={handleOpen}
        onClose={handleBlur}
        renderOption={(renderOptionProps, option) => (
          <li {...renderOptionProps}>
            {renderOption ? (
              renderOption(option)
            ) : (
              <OptionText option={option} inputValue={inputValue} />
            )}
          </li>
        )}
        slotProps={{
          listbox: {
            component: ListBox,
            ...{
              dataLength: items.length,
              next: requestNextItems,
              hasMore,
              loading: isItemsLoading,
              long: !!renderOption,
            },
          },
        }}
        renderInput={(params) => {
          return mask ? (
            <TextFieldMasked
              {...params}
              mask={mask}
              name={name}
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              loading={loading}
              onChange={handleInputChange as never}
            />
          ) : (
            <TextField
              {...params}
              value={inputValue}
              name={name}
              classes={{ root: classes.inputRoot }}
              autoComplete="off"
              variant="outlined"
              fullWidth
              error={error}
              helperText={helperText}
              placeholder={placeholder || label}
              {...(loading
                ? {
                    slotProps: {
                      input: {
                        endAdornment: <Preloader />,
                      },
                    },
                  }
                : {
                    InputProps: {
                      ...params.InputProps,
                      endAdornment: inputValue && (
                        <Box
                          cursor="pointer"
                          onClick={handleReset}
                          height={24}
                          pr="16"
                        >
                          <Icon
                            name="cancel_circle"
                            color="stroke.secondary"
                            size="20"
                          />
                        </Box>
                      ),
                    },
                  })}
            />
          );
        }}
      />

      {!!value?.length && (
        <Box display="flex" alignItems="center" flexWrap="wrap" gap="8" mt="16">
          {value.map((item, index) => (
            <Tag
              key={item}
              label={item}
              disabled={disabled}
              onDelete={handleDeleteTag(index)}
            />
          ))}
        </Box>
      )}
    </FormControl>
  );
};
